//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ContentLibraryFilter,
  IContentLibraryItem,
  IContentLibraryItemsDataResponse,
  IDeserializedContentLibraryItem,
  IMediaAdMetadata,
} from '@apis/content-library/contentLibraryApiModel';
import { isDefined } from '@utils/filterPredicates';
import { Serialized } from '@utils/types/Serialized';

export function isGif(item: IContentLibraryItem): boolean {
  const contentType = item.metadata.contentType;
  if (contentType) {
    return contentType === 'image/gif';
  }

  const numFrames = item.metadata.numFrames ?? 1;
  const isVideoItem = isVideo(item); // Ignore videos with GIF thumbnails

  return !isVideoItem && numFrames > 1;
}

export function isVideo(item: IContentLibraryItem): boolean {
  return !!item.metadata.videoSource;
}

export function deserializeAdMetadata(
  metadata?: Serialized<IMediaAdMetadata>,
): IMediaAdMetadata {
  return {
    ...metadata,
    adCode: isDefined(metadata?.adCode)
      ? {
          ...metadata.adCode,
          createdAt: new Date(metadata.adCode.createdAt),
          expirationTime: metadata.adCode.expirationTime
            ? new Date(metadata.adCode.expirationTime)
            : undefined,
        }
      : undefined,
  };
}

export const deserializeContentLibraryItemsDataResponse = (
  response: Serialized<IContentLibraryItemsDataResponse>,
): IContentLibraryItemsDataResponse => ({
  data: {
    ...response.data,
    contentItems: response.data.contentItems.map((item) => ({
      ...item,
      adMetadata: isDefined(item.adMetadata)
        ? deserializeAdMetadata(item.adMetadata)
        : undefined,
    })),
  },
});

// Use computedImpressions first, as it's the canonical field for sorting.
// This ensures consistency across views since impressions can often be missing from metadata.
// If computedImpressions is unavailable, fall back to videoViews for video content,
// and finally use impressions as a last resort.
export function getImpressionsValue(
  item: IDeserializedContentLibraryItem | IContentLibraryItem,
) {
  return (
    item.socialDetails?.stats?.computedImpressions ??
    item.socialDetails?.stats?.videoViews ??
    item.socialDetails?.stats?.impressions
  );
}

export const getDistributionFilter = (
  distributions: string[],
  isDistributed?: boolean,
): ContentLibraryFilter => {
  return {
    '@type': 'distribution',
    negated: isDefined(isDistributed) ? !isDistributed : undefined,
    distributions: distributions.map((distribution) => {
      if (
        distribution === 'like2buy' ||
        distribution === 'like2buy_v2' ||
        distribution === 's:facebook' ||
        distribution === 's:instagram' ||
        distribution === 's:pinterest' ||
        distribution === 's:youtube' ||
        distribution === 'reveal' ||
        distribution === 'revolve' ||
        distribution === 'showroom' ||
        distribution === 'googleAssetFeed'
      ) {
        return {
          '@type': distribution,
        };
      }
      return {
        '@type': 'galleries',
        reelId: distribution,
      };
    }),
  };
};
